import { useEffect, useRef, useState } from "react";
import Button from "../../ui/button";
import ToggleButton from "../../ui/button/toggleButton";
import Card from "../../ui/card";
import ReitFacilityMap from "./components/map";
import ReitFacilityTable from "./components/table";
import GroupButtons, { IItemButtonGroup } from "../../ui/button/groupButtons";
import useReitTypeList from "../../../api/hooks/hooks-reit-facility/use-facility-reit-type-list";

import styles from "./styles.module.scss";

export type TToggleButton = "map" | "table";

const ID_ALL_TYPES = -1;

const ReitFacility = () => {
  const [toggleButton, setToggleButton] = useState<TToggleButton>("table");
  const [facilityTypeIds, setFacilityTypeIds] = useState<number[]>([]);
  const [isResetSelectedMapItem, setIsResetSelectedMapItem] = useState(false);

  const handleChangeSelectedMapItem = (value: boolean) => {
    setIsResetSelectedMapItem(value);
  };

  const handleChangeFacilityTypes = (id: number) => {
    handleChangeSelectedMapItem(true);
    if (id === ID_ALL_TYPES) {
      setFacilityTypeIds([]);
    } else {
      setFacilityTypeIds([id]);
    }
  };

  const handleToggleButton = (data: TToggleButton) => {
    setToggleButton(data);
  };

  const { reitListType, error, isMutating, trigger } = useReitTypeList();

  useEffect(() => {
    trigger();
  }, []);

  const typeList = reitListType
    ?.sort((first, second) => first.id - second.id)
    ?.map((type) => {
      return {
        onClick: () => handleChangeFacilityTypes(type.id),
        label: type.fullTitle,
        variant: facilityTypeIds.includes(type.id) ? "default" : "lightGreen",
      };
    }) as IItemButtonGroup[];

  typeList?.unshift({
    onClick: () => handleChangeFacilityTypes(ID_ALL_TYPES),
    label: "Все",
    variant: !facilityTypeIds.length ? "default" : "lightGreen",
  } as IItemButtonGroup);

  const [height, setHeight] = useState<number>();

  const observedDiv = useRef<HTMLDivElement | null>(null);
  const sizesRef = useRef({ height: -1 });

  useEffect(() => {
    if (!observedDiv.current) {
      return;
    }
    const resizeObserver = new ResizeObserver(() => {
      if (
        observedDiv.current &&
        observedDiv.current.offsetHeight !== sizesRef.current.height
      ) {
        sizesRef.current.height = observedDiv.current.offsetHeight;
        const windowHeight = window.innerHeight;
        setHeight(windowHeight - observedDiv.current.offsetHeight - 30 - 28);
      }
    });
    resizeObserver.observe(observedDiv.current);
    return () => {
      resizeObserver.disconnect();
    };
  }, [observedDiv.current]);

  const isPhone = window.innerWidth < 600;

  const heightContent = isPhone ? 600 : 750;

  return (
    <div className={styles.reitFacility}>
      <div className={styles.reitFacility__container}>
        <div ref={observedDiv}>
          <div className={styles.reitFacility__toggle}>
            <ToggleButton
              data={[
                <Button
                  label="Карта"
                  onClick={() => handleToggleButton("map")}
                  variant={toggleButton === "map" ? "default" : "gray"}
                />,
                <Button
                  label="Список"
                  onClick={() => handleToggleButton("table")}
                  variant={toggleButton === "table" ? "default" : "gray"}
                />,
              ]}
            />
          </div>
          {typeList?.length && (
            <div className={styles.reitFacility__selectButtons}>
              <GroupButtons isRowContainer data={typeList} />
            </div>
          )}
        </div>

        {toggleButton === "map" && (
          <div className={styles.reitFacility__map}>
            <Card>
              <ReitFacilityMap
                facilityTypeIds={facilityTypeIds}
                isLoadingType={isMutating}
                heightMap={heightContent}
                isResetSelectedMapItem={isResetSelectedMapItem}
                onResetSelectedMapItem={() =>
                  handleChangeSelectedMapItem(false)
                }
              />
            </Card>
          </div>
        )}
        {toggleButton === "table" && (
          <div className={styles.reitFacility__table}>
            <Card>
              <ReitFacilityTable
                facilityTypeIds={facilityTypeIds}
                isLoadingType={isMutating}
                heightTable={heightContent}
              />
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReitFacility;
